import type { Expense } from 'src/types/types';

import { toast } from 'sonner';
import { useBoolean, usePopover } from 'minimal-shared/hooks';

import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';

import { RouterLink } from 'src/routes/components';

import { formatDate } from 'src/utils/utils';

import { createExpense } from 'src/data/api';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { setAllExpenses } from 'src/features/expenseSlice/expenseSlice';

import { Iconify } from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { CustomPopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

type Props = {
  row: Expense;
  selected: boolean;
  editHref: string;
  onDeleteRow: () => void;
};

export function ExpenseTableRow({ row, selected, editHref, onDeleteRow }: Props) {
  const menuActions = usePopover();
  const confirmDialog = useBoolean();
  const dispatch = useAppDispatch();
  const expenses = useAppSelector((state) => state.expenses.expenses);

  const updateMoms = (orderId: string, hasMoms: boolean) => {
    createExpense({ ...row, hasMoms })
      .then(() => {
        toast.success('Moms blev opdateret');
        const updatedExpenses = expenses.map((expense) =>
          expense.id === orderId ? { ...expense, hasMoms } : expense
        );
        dispatch(setAllExpenses(updatedExpenses));
      })
      .catch((err) => {
        toast.error('Moms blev ikke opdateret');
      });
  };

  const renderMenuActions = () => (
    <CustomPopover
      open={menuActions.open}
      anchorEl={menuActions.anchorEl}
      onClose={menuActions.onClose}
      slotProps={{ arrow: { placement: 'right-top' } }}
    >
      <MenuList>
        <li>
          <MenuItem component={RouterLink} href={editHref} onClick={menuActions.onClose}>
            <Iconify icon="solar:pen-bold" />
            Rediger
          </MenuItem>
        </li>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={() => {
            confirmDialog.onTrue();
            menuActions.onClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          Slet
        </MenuItem>
      </MenuList>
    </CustomPopover>
  );

  const renderConfirmDialog = () => (
    <ConfirmDialog
      open={confirmDialog.value}
      onClose={confirmDialog.onFalse}
      title="Slet udgift"
      content="Er du sikker på at du vil slette denne udgift?"
      action={
        <Button variant="contained" color="error" onClick={onDeleteRow}>
          Slet
        </Button>
      }
    />
  );

  return (
    <>
      <TableRow hover selected={selected}>
        <TableCell>{row.id.slice(0, 8)}</TableCell>
        <TableCell>{row.name}</TableCell>

        <TableCell>
          <ListItemText
            primary={formatDate(row.date)}
            slotProps={{
              primary: { noWrap: true, sx: { typography: 'body2' } },
              secondary: { sx: { mt: 0.5, typography: 'caption' } },
            }}
          />
        </TableCell>

        <TableCell>{row.price} kr.</TableCell>
        <TableCell align="center">{row.quantity}</TableCell>
        <TableCell>{(row.price * row.quantity).toFixed(2)} kr.</TableCell>
        <TableCell align="left">{row.note}</TableCell>
        <TableCell onDoubleClick={() => updateMoms(row.id, !row.hasMoms)}>
          {row.hasMoms ? (
            <Iconify icon="eva:checkmark-fill" color="green" />
          ) : (
            <Iconify icon="carbon:close" color="red" />
          )}
        </TableCell>
        <TableCell align="left">
          {row.expenseUrl && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => window.open(row.expenseUrl ?? '', '_blank', 'noopener,noreferrer')}
            >
              Se bilag
            </Button>
          )}
        </TableCell>

        <TableCell align="right" sx={{ px: 1 }}>
          <IconButton color={menuActions.open ? 'inherit' : 'default'} onClick={menuActions.onOpen}>
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      {renderMenuActions()}
      {renderConfirmDialog()}
    </>
  );
}
